import { ApiOutlined, ControlOutlined, SafetyOutlined, TeamOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { getConfig } from '@shared/lib/config';
import { Card, Col, Row, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const { Title, Text } = Typography;

type TileProps = {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};

/**
 * Карточка-плитка с переходом на раздел.
 */
function Tile({ to, title, description, icon }: TileProps) {
  return (
    <Link to={to} style={{ display: 'block' }}>
      <Card
        hoverable
        style={{
          height: '100%',
          borderColor: designTokens.slate200,
          transition: 'border-color 0.15s ease, transform 0.15s ease',
        }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: designTokens.slate100,
              color: designTokens.navy900,
              display: 'grid',
              placeItems: 'center',
              fontSize: 18,
            }}
          >
            {icon}
          </div>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 15, color: designTokens.navy900 }}>
              {title}
            </Text>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
              {description}
            </Text>
          </Space>
        </Space>
      </Card>
    </Link>
  );
}

/**
 * Главная страница админки. В MVP — только список доступных разделов
 * и краткий статус окружения.
 */
export function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const cfg = getConfig();

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
          {t('common:menu.dashboard')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {cfg.appName} · {cfg.env.toUpperCase()}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Tile
            to="/settings"
            title={t('common:menu.settings')}
            description={t('dashboard:tiles.settings')}
            icon={<ControlOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Tile
            to="/users"
            title={t('common:menu.users')}
            description={t('dashboard:tiles.users')}
            icon={<TeamOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Tile
            to="/rbac"
            title={t('common:menu.rbac')}
            description={t('dashboard:tiles.rbac')}
            icon={<SafetyOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card style={{ borderColor: designTokens.slate200, height: '100%' }}>
            <Space direction="vertical" size={12}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: designTokens.slate100,
                  color: designTokens.slate500,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 18,
                }}
              >
                <ApiOutlined />
              </div>
              <Text strong style={{ fontSize: 15 }}>
                {t('dashboard:env.title')}
              </Text>
              <Space direction="vertical" size={2} style={{ fontSize: 13 }}>
                <Text type="secondary">
                  {t('dashboard:env.apiLabel')}: <Text code>{cfg.apiBaseUrl || '/api (proxy)'}</Text>
                </Text>
                <Text type="secondary">
                  {t('dashboard:env.envLabel')}: <Text code>{cfg.env}</Text>
                </Text>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
